import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faUpload, faEdit } from "@fortawesome/free-solid-svg-icons";

import EditLogoPopup from "./auxiliar/EditLogoPopup";
import CustomForm from "../general/form/CustomForm";
import ModalConfirm from "../general/auxiliar/modal/ModalConfirm";
import useFormWithDisable from "../../auxiliar/customHooks/useFormWithDisable";
import { deleteClinic, putClinic, getClinic } from "../../actions/clinics";
import {
    checkCIFFormat,
    checkMailFormat,
    checkPhoneNumberFormat,
} from "../../auxiliar/formatValidators";
import DetailsSkeleton from "../general/auxiliar/skeleton/DetailsSkeleton";

const Details = () => {
    const { clinic_id: clinicId } = useParams();
    const navigate = useNavigate();

    const modalId = "delete-clinic-modal";
    const {
        register,
        watch,
        handleSubmit,
        handleSubmitWithoutValidation,
        openModal,
        setValue,
        fetchFormValues,
        resetFetchedValues,
        errors,
        isSubmitting,
        originalValues,
        isLoadingData,
    } = useFormWithDisable(true, modalId);

    const logoUrl = watch("logo_url") || originalValues.logo_url;
    const invoiceCode = watch("invoice_code");
    const invoiceNumber = watch("invoice_number");

    const invoiceInfo =
        invoiceCode && invoiceNumber
            ? `La primera factura generada será ${invoiceCode}-${invoiceNumber}`
            : undefined;

    const activeClinic = useSelector((state) => state.activeClinic);
    const userType = useSelector((state) => state.auth.user_type);

    const [isEditing, setIsEditing] = useState(false);

    const formConfigVertical = {
        name: {
            condition: true,
            disabled: !isEditing,
            type: "text",
            label: "Nombre *",
            placeholder: "Nombre",
            name: "name",
            validators: { required: "El nombre es obligatorio" },
        },
        email: {
            condition: true,
            disabled: !isEditing,
            type: "text",
            label: "Correo Electrónico *",
            placeholder: "clinica@email.com",
            name: "email",
            validators: {
                required: "El correo es obligatorio",
                validate: checkMailFormat,
            },
        },
        landline_phone: {
            condition: true,
            disabled: !isEditing,
            type: "text",
            label: "Número de teléfono fijo",
            placeholder: "XXX XXX XXX",
            name: "landline_phone",
            validators: { validate: checkPhoneNumberFormat },
        },
        mobile_phone: {
            condition: true,
            disabled: !isEditing,
            type: "text",
            label: "Número de teléfono móvil",
            placeholder: "+34 XXX XXX XXX",
            name: "mobile_phone",
            validators: { validate: checkPhoneNumberFormat },
        },
    };

    const formConfigFullWidth = {
        address: {
            condition: true,
            disabled: !isEditing,
            type: "text",
            label: "Dirección *",
            placeholder: "Calle, número",
            name: "address",
        },
        town: {
            condition: true,
            disabled: !isEditing,
            type: "text",
            label: "Ciudad",
            placeholder: "Ciudad",
            name: "town",
        },
        invoice_code: {
            condition: true,
            disabled: !isEditing,
            info: invoiceInfo,
            type: "text",
            label: "Código de factura *",
            placeholder: "EX",
            name: "invoice_code",
            validators: {
                required: "El código de factura es obligatorio",
                minLength: {
                    value: 2,
                    message: "El código de factura debe tener entre 2 y 5 caracteres",
                },
                maxLength: {
                    value: 5,
                    message: "El código de factura debe tener entre 2 y 5 caracteres",
                },
            },
        },
        invoice_number: {
            condition: true,
            disabled: !isEditing,
            info: invoiceInfo,
            type: "number",
            label: "Número de factura inical *",
            placeholder: "Ejemplo: 0",
            name: "invoice_number",
            minValue: 0,
            validators: {
                required: "El número de factura inicial es obligatorio",
                min: {
                    value: 0,
                    message: "El número de factura inicial no puede ser negativo",
                },
            },
        },
        cif: {
            condition: true,
            disabled: !isEditing,
            info: "El identificador fiscal es necasario para la generación de facturas",
            type: "text",
            label: "CIF *",
            placeholder: "00000000X",
            name: "cif",
            validators: { validate: checkCIFFormat },
        },
        dummy: {
            condition: true,
            type: "dummy",
        },
        task_generation: {
            condition: true,
            disabled: !isEditing,
            info: "Si activas la creación de recordatorios se crearán tareas en los cumpleaños de los pacientes",
            label: "Activar creación de recordatorios",
            type: "checkbox",
            name: "task_generation",
        },
        future_debt: {
            condition: true,
            disabled: !isEditing,
            info: "Si lo activas, se contabilizarán las citas futuras como deuda antes de que se hayan producido",
            label: "Contabilizar deuda futura",
            type: "checkbox",
            name: "future_debt",
        },
    };

    const refreshLogoUrl = (logoUrl) => {
        setValue("logo_url", logoUrl);
    };

    useEffect(() => {
        if (clinicId) fetchFormValues(getClinic, clinicId);
    }, [clinicId]);

    useEffect(() => {
        if (!isEditing && clinicId) resetFetchedValues();
    }, [isEditing]);

    return (
        <section>
            <h2 className="sr-only">
                Detalles de tu clínica de fisioterapia – Configura tu clínica con MiFisio Agenda
            </h2>

            <h3 className="text-primary text-2xl">Detalles de la Clínica</h3>

            {/* Form */}
            <form className="my-3">
                <div className="grid grid-cols-1 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3">
                    <CustomForm
                        formConfig={formConfigVertical}
                        errors={errors}
                        register={register}
                        className="grid grid-cols-1 sm:col-span-2 md:col-span-3 lg:col-span-4 gap-3"
                        isLoading={isLoadingData}
                    />

                    {/* Logo */}
                    <div className="bg-base-200 shadow-xl rounded-lg p-8 my-5 text-center flex items-center justify-center aspect-square w-full self-center">
                        {isLoadingData ? (
                            <DetailsSkeleton />
                        ) : logoUrl && logoUrl !== "deleted" ? (
                            <span className="flex flex-col items-center justify-center">
                                <img
                                    src={logoUrl}
                                    alt="Logo de la clínica"
                                    className="object-contain rounded"
                                />

                                <EditLogoPopup
                                    originalLogoURL={logoUrl}
                                    clinicId={clinicId}
                                    refreshLogoUrl={refreshLogoUrl}
                                    trigger={
                                        <button
                                            type="button"
                                            className="text-primary/80 hover:text-primary hover:cursor-pointer my-1"
                                            title="Añadir logo"
                                        >
                                            <p className="text-sm">
                                                Editar Logo <FontAwesomeIcon icon={faEdit} />
                                            </p>
                                        </button>
                                    }
                                />
                            </span>
                        ) : (
                            <EditLogoPopup
                                originalLogoURL={logoUrl}
                                clinicId={clinicId}
                                refreshLogoUrl={refreshLogoUrl}
                                trigger={
                                    <button
                                        type="button"
                                        className="text-primary/90 hover:text-primary hover:cursor-pointer"
                                        title="Añadir logo"
                                    >
                                        <FontAwesomeIcon icon={faUpload} className="text-6xl" />
                                        <p className="text-lg my-4">Añadir logo</p>
                                    </button>
                                }
                            />
                        )}
                    </div>
                </div>

                <CustomForm
                    formConfig={formConfigFullWidth}
                    errors={errors}
                    register={register}
                    className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-3"
                    isLoading={isLoadingData}
                />

                {userType === "manager" && (
                    <div className="mt-5 flex space-x-2 sm:space-x-4">
                        {!isEditing ? (
                            <>
                                <button
                                    className="btn btn-primary btn-sm"
                                    onClick={handleSubmitWithoutValidation(setIsEditing, true)}
                                    disabled={isSubmitting}
                                >
                                    Editar
                                </button>
                                <button
                                    className="btn btn-error btn-sm"
                                    onClick={openModal}
                                    disabled={isSubmitting}
                                >
                                    Eliminar
                                </button>
                                <button
                                    className="btn btn-primary btn-sm"
                                    onClick={handleSubmitWithoutValidation(navigate, -1)}
                                    disabled={isSubmitting}
                                >
                                    Atrás
                                </button>
                            </>
                        ) : (
                            <>
                                <button
                                    className="btn btn-primary btn-sm"
                                    onClick={handleSubmit(
                                        putClinic,
                                        activeClinic.id,
                                        handleSubmitWithoutValidation(setIsEditing, false),
                                    )}
                                    disabled={isSubmitting}
                                >
                                    Guardar
                                </button>
                                <button
                                    className="btn btn-primary btn-sm"
                                    onClick={handleSubmitWithoutValidation(setIsEditing, false)}
                                    disabled={isSubmitting}
                                >
                                    Cancelar
                                </button>
                            </>
                        )}
                    </div>
                )}
            </form>

            <ModalConfirm
                id={modalId}
                title="¿Estás seguro de que quieres eliminar todos los datos de la clínica?"
                text="No podremos recuperar la información más adelante."
                onConfirm={handleSubmitWithoutValidation(deleteClinic, activeClinic.id)}
            />
        </section>
    );
};

export default Details;
